import React, { useEffect, useRef } from 'react';
import { Rive, Layout, Fit, Alignment } from '@rive-app/canvas';

interface RiveSignatureAnimationProps {
  riveFileUrl: string; 
  width?: number;
  height?: number;
}

export const RiveSignatureAnimation: React.FC<RiveSignatureAnimationProps> = ({
  riveFileUrl,
  width = 300,
  height = 150,
}) => {
  const canvasRef = useRef<HTMLCanvasElement | null>(null);
  const riveInstanceRef = useRef<Rive | null>(null);

  useEffect(() => {
    if (!canvasRef.current) return;

    let isMounted = true;

    const loadRive = async () => {
      const response = await fetch(riveFileUrl);
      const buffer = await response.arrayBuffer();

      if (!isMounted) return;

      riveInstanceRef.current = new Rive({
        buffer,
        autoplay: true,
        canvas: canvasRef.current!,
        layout: new Layout({
          fit: Fit.Contain,
          alignment: Alignment.Center,
        }),
        useOffscreenRenderer: true,
        onLoad: () => {
          riveInstanceRef.current?.resizeDrawingSurfaceToCanvas();
        },
      });
    };

    loadRive();

    const handleResize = () => {
      riveInstanceRef.current?.resizeDrawingSurfaceToCanvas();
    };
    window.addEventListener('resize', handleResize);

    return () => {
      isMounted = false;
      window.removeEventListener('resize', handleResize);
      riveInstanceRef.current?.stop();
      riveInstanceRef.current = null;
    };
  }, [riveFileUrl]);

  return (
    <canvas
      ref={canvasRef}
      width={width}
      height={height}
      style={{ width: '100%', height: 'auto', maxWidth: width, background: 'transparent' }}
    />
  );
};
